// SPDX-License-Identifier: AGPL-3.0-or-later

import Guilds from '@app/features/guild/state/Guilds';
import {loadGuildTasks} from '@app/features/tasks/commands/TaskCommands';
import {TaskEditDialog} from '@app/features/tasks/components/TaskEditDialog';
import styles from '@app/features/tasks/components/TaskMentionsActivity.module.css';
import TaskMentions from '@app/features/tasks/state/TaskMentions';
import Tasks from '@app/features/tasks/state/Tasks';
import {Avatar} from '@app/features/ui/components/Avatar';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const HEADING = msg({message: 'Tagged in to-dos', comment: 'Activity page: comments on to-dos where someone tagged you.'});
const MENTIONED_YOU_ON = msg({message: 'mentioned you on', comment: 'Between a name and a to-do title.'});

function when(iso: string, locale: string): string {
	const date = new Date(iso);
	return date.toDateString() === new Date().toDateString()
		? date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'})
		: date.toLocaleDateString(locale, {month: 'short', day: 'numeric'});
}

/** Activity: comments on to-dos where someone tagged you. Click one to open the to-do. */
export const TaskMentionsActivity = observer(function TaskMentionsActivity() {
	const {i18n} = useLingui();
	const [openTaskId, setOpenTaskId] = useState<string | null>(null);
	const mentions = TaskMentions.all.slice(0, 30);

	useEffect(() => {
		for (const guildId of Guilds.getGuildIds()) void loadGuildTasks(guildId);
	}, []);

	// Opening Activity counts as seeing them; the dots stay until you leave.
	useEffect(() => {
		const timer = window.setTimeout(() => TaskMentions.markAllSeen(), 1500);
		return () => window.clearTimeout(timer);
	}, [mentions.length]);

	const openTask = openTaskId
		? Guilds.getGuildIds()
				.flatMap((guildId) => Tasks.getAllGuildTasks(guildId))
				.find((task) => task.id === openTaskId)
		: undefined;

	if (mentions.length === 0) return null;
	return (
		<section className={styles.root} data-flx="tasks.task-mentions-activity.root">
			<h2 className={styles.heading} data-flx="tasks.task-mentions-activity.heading">
				{i18n._(HEADING)}
			</h2>
			<ul className={styles.list} data-flx="tasks.task-mentions-activity.list">
				{mentions.map((mention) => {
					const author = Users.getUser(mention.authorId);
					const unseen = TaskMentions.isUnseen(mention);
					return (
						<li key={mention.commentId} data-flx="tasks.task-mentions-activity.item">
							<button
								type="button"
								className={styles.item}
								onClick={() => setOpenTaskId(mention.task.id)}
								data-flx="tasks.task-mentions-activity.item.open.button"
							>
								{author ? (
									<Avatar user={author} size={32} data-flx="tasks.task-mentions-activity.avatar" />
								) : (
									<span className={styles.avatarBlank} data-flx="tasks.task-mentions-activity.avatar-blank" />
								)}
								<span className={styles.body} data-flx="tasks.task-mentions-activity.body">
									<span className={styles.line} data-flx="tasks.task-mentions-activity.line">
										<b data-flx="tasks.task-mentions-activity.author">{author?.globalName || author?.username || '…'}</b>{' '}
										{i18n._(MENTIONED_YOU_ON)}{' '}
										<b data-flx="tasks.task-mentions-activity.title">{mention.task.title}</b>
									</span>
									<span className={styles.text} data-flx="tasks.task-mentions-activity.text">
										{mention.content}
									</span>
								</span>
								<span className={styles.time} data-flx="tasks.task-mentions-activity.time">
									{when(mention.createdAt, i18n.locale)}
								</span>
								{unseen && <span className={styles.dot} aria-hidden="true" data-flx="tasks.task-mentions-activity.dot" />}
							</button>
						</li>
					);
				})}
			</ul>
			{openTask && (
				<TaskEditDialog
					task={openTask}
					onClose={() => setOpenTaskId(null)}
					data-flx="tasks.task-mentions-activity.dialog"
				/>
			)}
		</section>
	);
});
